'use client';

import {
  Group,
  Progress,
  Button,
  Loader,
  Center,
  Paper,
  Text,
  Stack,
  Box,
  Badge,
  Tooltip,
} from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { signIn } from 'next-auth/react';
import MentorshipGoalStep from '@/components/mentorshipApplication/steps/MentorshipGoalStep';
import MentorshipTimelineStep from '@/components/mentorshipApplication/steps/MentorshipTimelineStep';
import MessageStep from '@/components/mentorshipApplication/steps/MessageStep';
import AuthStep from '@/components/mentorshipApplication/steps/AuthStep';
import EmailVerificationStep from '@/components/mentorshipApplication/steps/EmailVerificationStep';
import LinkedInStep from '@/components/mentorshipApplication/steps/LinkedInStep';
import PaymentStep from '@/components/mentorshipApplication/steps/PaymentStep';
import ReviewStep from '@/components/mentorshipApplication/steps/ReviewStep';
import { MentorshipApplicationPayload, RequestQuestion } from '@/types/mentorship';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { Avatar } from '@/components/Avatar';
import classes from './UnifiedMentorshipApplication.module.css';
import { ExpertiseBadgeGroup, UserBadgeGroup, UserBadge } from '@/components/badges';
import MobileApplicationHeader from './MobileApplicationHeader';

interface UnifiedMentorshipApplicationProps {
  mentorUser: any;
  plan?: string;
}

interface AuthForm {
  name: string;
  email: string;
  password: string;
  location: string;
  linkedinUrl?: string;
  agreedToTerms?: boolean;
}

interface ApplicationForm {
  mentorshipGoal: string;
  mentorshipTimeline: string;
  message: string;
}

type FlowStep = 
  | 'mentorshipGoal' 
  | 'mentorshipTimeline' 
  | 'message' 
  | 'auth' 
  | 'emailVerification' 
  | 'linkedin'
  | 'payment' 
  | 'review';

export default function UnifiedMentorshipApplication({
  mentorUser,
  plan = 'standard'
}: UnifiedMentorshipApplicationProps) {
  const { user, userLoading, refresh } = useCurrentUser();
  const [currentStep, setCurrentStep] = useState<FlowStep>('mentorshipGoal');
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signup');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [paymentSetup, setPaymentSetup] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [applicationToken, setApplicationToken] = useState<string | null>(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [blockingError, setBlockingError] = useState<string | null>(null);
  
  // Message validation state
  const [messageValidation, setMessageValidation] = useState<any>(null);
  const [isValidatingMessage, setIsValidatingMessage] = useState(false);
  const [validationChecked, setValidationChecked] = useState(false);

  // Ref for scrolling to mobile header on step change
  const mobileHeaderRef = useRef<HTMLDivElement>(null);

  // Scroll to top when step changes (different behavior for mobile vs desktop)
  const scrollToTop = useCallback(() => {
    const isMobile = window.innerWidth < 768; // Mobile breakpoint at 768px

    if (isMobile && mobileHeaderRef.current) {
      // On mobile: scroll to "Apply for Mentorship with..." headline
      const elementTop = mobileHeaderRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementTop - 20, // 20px offset from the top
        behavior: 'smooth'
      });
    } else {
      // On desktop: scroll to top of page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Application form
  const applicationForm = useForm<ApplicationForm>({
    initialValues: {
      mentorshipGoal: '',
      mentorshipTimeline: '',
      message: '',
    },
    validate: {
      mentorshipGoal: (value) => (value === '' ? 'Please select a goal' : null),
      mentorshipTimeline: (value) => (value === '' ? 'Please select a timeline' : null),
      message: (value) => (value === '' ? 'Please write a message' : null),
    },
  });

  // Auth form
  const authForm = useForm<AuthForm>({
    initialValues: {
      name: '',
      email: '',
      password: '',
      location: '',
      linkedinUrl: '',
      agreedToTerms: false,
    },
    validate: {
      name: (value) => (value.length < 2 ? 'Name is too short' : null),
      email: (value) => (value.includes('@') && value.includes('.') ? null : 'Invalid email'),
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null),
      location: (value) => (value.length < 2 ? 'Location is required' : null),
      agreedToTerms: (value) => (authMode === 'signup' && !value ? 'You must agree to the terms to continue' : null),
    },
  });

  const allSteps: FlowStep[] = useMemo(() => {
    const steps: FlowStep[] = [
      'mentorshipGoal', 
      'mentorshipTimeline', 
      'message'
    ];
    
    // Add auth step if user is not logged in
    if (!user) {
      steps.push('auth');
    }
    
    // Add email verification step if user is logged in but email not verified
    if (user && !user.emailVerified) {
      steps.push('emailVerification');
    }
    
    // Add LinkedIn step after email verification (or after auth if no email verification needed)
    steps.push('linkedin');
    
    steps.push('payment', 'review');
    return steps;
  }, [user]);
  const currentStepIndex = allSteps.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) * 100) / allSteps.length;

  // Load existing application data
  useEffect(() => {
    const loadExistingApplication = async () => {
      if (userLoading) return;
      
      // Only run once when component mounts or user changes
      if (isLoading === false) return;

      // Check for existing application token in localStorage
      const token = localStorage.getItem('mentorshipApplicationToken');
      
      // Validate token if it exists
      if (token) {
        try {
          const response = await fetch(`/api/mentorshipApplications/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              token, 
              mentorUserId: mentorUser.id,
              menteeUserId: user?.id 
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.valid) {
              setApplicationToken(token);
              
              // Populate form with existing data
              const answers = JSON.parse(data.application.answersJson || '[]');
              answers.forEach((answer: RequestQuestion) => {
                applicationForm.setFieldValue(answer.question as keyof ApplicationForm, answer.answer || '');
              });
              
              // Determine current step based on completed answers
              const hasGoal = answers.some((a: RequestQuestion) => a.question === 'mentorshipGoal' && a.answer && a.answer.trim() !== '');
              const hasTimeline = answers.some((a: RequestQuestion) => a.question === 'mentorshipTimeline' && a.answer && a.answer.trim() !== '');
              const hasMessage = answers.some((a: RequestQuestion) => a.question === 'message' && a.answer && a.answer.trim() !== '');
              
              if (!hasGoal) {
                setCurrentStep('mentorshipGoal');
              } else if (!hasTimeline) {
                setCurrentStep('mentorshipTimeline');
              } else if (!hasMessage) {
                setCurrentStep('message');
              } else if (!user) {
                // All initial steps done but not logged in
                setCurrentStep('auth');
              } else if (user && !user.emailVerified) {
                // User logged in but email not verified
                setCurrentStep('emailVerification');
              } else {
                // User logged in and email verified, check payment status
                const hasValidPayment = await checkPaymentStatus();
                if (hasValidPayment) {
                  setPaymentSetup(true);
                  setCurrentStep('review');
                } else {
                  setCurrentStep('payment');
                }
              }
            } else {
              // Invalid token, clear it
              localStorage.removeItem('mentorshipApplicationToken');
              localStorage.removeItem('mentorshipApplicationMentorId');
              setApplicationToken(null);
            }
          } else {
            // Token validation failed, clear it
            localStorage.removeItem('mentorshipApplicationToken');
            localStorage.removeItem('mentorshipApplicationMentorId');
            setApplicationToken(null);
          }
        } catch (error) {
          console.error('Error validating token:', error);
          // Clear invalid token
          localStorage.removeItem('mentorshipApplicationToken');
          localStorage.removeItem('mentorshipApplicationMentorId');
          setApplicationToken(null);
        }
      }

      if (user && !token) {
        // User is logged in and no token, check for blocking conditions first
        try {
          // Check for existing active mentorship
          const mentorshipResponse = await fetch(`/api/mentorships?menteeUserId=${user.id}&mentorUserId=${mentorUser.id}`);
          if (mentorshipResponse.ok) {
            const mentorships = await mentorshipResponse.json();
            if (mentorships && mentorships.length > 0) {
              setBlockingError('You already have an active mentorship with this mentor.');
              setIsLoading(false);
              return;
            }
          }

          // Check for active mentorship first - redirect if exists
          const applicationsResponse = await fetch(`/api/mentorshipApplications?menteeUserId=${user.id}&mentorUserId=${mentorUser.id}`);
          if (applicationsResponse.ok) {
            const allApplications = await applicationsResponse.json();
            
            // If there's an ACCEPTED application, redirect to messages
            const activeMentorship = allApplications.find((app: any) => app.status === 'ACCEPTED');
            if (activeMentorship) {
              // Clear any stored application tokens
              localStorage.removeItem('mentorshipApplicationToken');
              localStorage.removeItem('mentorshipApplicationMentorId');
              // Redirect to messages
              window.location.href = `/learning/messages/${mentorUser.id}`;
              return;
            }
            
            // Check for recent rejections (within last 7 days)
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            const recentRejection = allApplications.find((app: any) => 
              app.status === 'REJECTED' && new Date(app.updatedAt) >= sevenDaysAgo
            );
            if (recentRejection) {
              setBlockingError('You cannot reapply to this mentor within 7 days of a rejection.');
              setIsLoading(false);
              return;
            }
          }

          // Check for existing application by user ID
          const params = new URLSearchParams({
            mentorUserId: mentorUser.id,
            menteeUserId: user.id,
          });
          const response = await fetch(`/api/mentorshipApplications?${params}`);
          const applications = await response.json();
          
          if (applications.length > 0) {
            const app = applications[0];
            const answers = JSON.parse(app.answersJson || '[]');
            
            // Always populate form with saved data from database
            answers.forEach((answer: RequestQuestion) => {
              applicationForm.setFieldValue(answer.question as keyof ApplicationForm, answer.answer);
            });

            // Determine step based on what's completed
            const hasGoal = answers.some((a: RequestQuestion) => a.question === 'mentorshipGoal' && a.answer && a.answer.trim() !== '');
            const hasTimeline = answers.some((a: RequestQuestion) => a.question === 'mentorshipTimeline' && a.answer && a.answer.trim() !== '');
            const hasMessage = answers.some((a: RequestQuestion) => a.question === 'message' && a.answer && a.answer.trim() !== '');
            
            if (!hasGoal) {
              setCurrentStep('mentorshipGoal');
            } else if (!hasTimeline) {
              setCurrentStep('mentorshipTimeline');
            } else if (!hasMessage) {
              setCurrentStep('message');
            } else if (!user.emailVerified) {
              // All questions answered but email not verified
              setCurrentStep('emailVerification');
            } else {
              // All questions answered and email verified, check LinkedIn then payment
              const hasValidPayment = await checkPaymentStatus();
              if (hasValidPayment) {
                setPaymentSetup(true);
                setCurrentStep('review');
              } else {
                setCurrentStep('linkedin');
              }
            }
          } else {
            // No existing application, start from beginning
            setCurrentStep('mentorshipGoal');
          }
        } catch (error) {
          console.error('Error loading existing application:', error);
          setCurrentStep('mentorshipGoal');
        }
      } else if (!user && !token) {
        // No user and no token, start from beginning
        setCurrentStep('mentorshipGoal');
      }
      
      setIsLoading(false);
    };

    // Restore application state after LinkedIn redirect
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const linkedinSuccess = urlParams.get('linkedin_success');
      
      if (linkedinSuccess === 'true') {
        // User just came back from LinkedIn - restore to linkedin step
        const savedStep = sessionStorage.getItem('application_step_before_linkedin');
        if (savedStep) {
          setCurrentStep('linkedin');
          sessionStorage.removeItem('application_step_before_linkedin');
        }
      }
    }

    loadExistingApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // isLoading is intentionally not included to prevent infinite loops (the effect sets isLoading)
    // applicationForm is stable from useForm and doesn't need to be included
  }, [user, userLoading, mentorUser.id]);

  const checkPaymentStatus = async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/billing/hasValidPaymentDetails');
      const data = await response.json();
      return data.result === true;
    } catch {
      return false;
    }
  };

  // Poll for email verification every 3 seconds when on email verification step
  useEffect(() => {
    if (currentStep === 'emailVerification') {
      const poll = async () => {
        await refresh();
      };
      
      const interval = setInterval(poll, 3000);
      
      return () => clearInterval(interval);
    }
  }, [currentStep, refresh]);

  // Auto-detect email verification and advance to LinkedIn step
  useEffect(() => {
    if (user && user.emailVerified && currentStep === 'emailVerification') {
      setEmailVerified(true);
      setTimeout(() => {
        setCurrentStep('linkedin');
        scrollToTop();
      }, 0);
    }
  }, [user?.emailVerified, currentStep, user, scrollToTop]);

  // Load user's existing LinkedIn URL when logged in
  useEffect(() => {
    if (user && !authForm.values.linkedinUrl) {
      const loadLinkedInUrl = async () => {
        try {
          const response = await fetch('/api/user/linkedin');
          if (response.ok) {
            const data = await response.json();
            if (data.linkedinUrl) {
              authForm.setFieldValue('linkedinUrl', data.linkedinUrl);
            }
          }
        } catch (error) {
          console.error('Error loading LinkedIn URL:', error);
        }
      };
      loadLinkedInUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Add marker class to body to hide main header shadow on mobile
  useEffect(() => {
    document.body.classList.add('mentorship-application-page');
    return () => {
      document.body.classList.remove('mentorship-application-page');
    };
  }, []);

  const saveApplicationProgress = useCallback(async () => {
    const answersJson = Object.entries(applicationForm.values).map(([key, value]) => ({
      question: key,
      answer: value,
    }));
    const payload: MentorshipApplicationPayload = {
      mentorUserId: mentorUser.id,
      answersJson,
      plan,
    };

    try {
      if (user) {
        // User is authenticated - use credentials-based API calls
        const params = new URLSearchParams({
          mentorUserId: mentorUser.id,
          menteeUserId: user.id,
        });
        const getResponse = await fetch(`/api/mentorshipApplications?${params}`, {
          credentials: 'include',
        });
        const existingApps = await getResponse.json();

        if (existingApps.length > 0) {
          // Update existing application
          const updateResponse = await fetch(`/api/mentorshipApplications/${existingApps[0].id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ answersJson }),
            credentials: 'include',
          });
          
          if (updateResponse.ok) {
            const updatedApp = await updateResponse.json();
            
            // For logged-in users, we should still store token for banner functionality
            if (updatedApp.token) {
                setApplicationToken(updatedApp.token);
              localStorage.setItem('mentorshipApplicationToken', updatedApp.token);
              localStorage.setItem('mentorshipApplicationMentorId', mentorUser.id);
            }
          }
        } else {
          // Create new application for authenticated user
          const createResponse = await fetch('/api/mentorshipApplications', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, menteeUserId: user.id }),
            credentials: 'include',
          });
          
          if (createResponse.ok) {
            const newApp = await createResponse.json();
            
            // Store token for banner functionality
            if (newApp.token) {
              setApplicationToken(newApp.token);
              localStorage.setItem('mentorshipApplicationToken', newApp.token);
              localStorage.setItem('mentorshipApplicationMentorId', mentorUser.id);
            }
          }
        }
      } else {
        // No user - use token-based saving (no credentials)
        if (applicationToken) {
          await fetch(`/api/mentorshipApplications/${applicationToken}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } else {
          const response = await fetch('/api/mentorshipApplications', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          
          if (response.ok) {
            const newApp = await response.json();
            if (newApp.token) {
              setApplicationToken(newApp.token);
              localStorage.setItem('mentorshipApplicationToken', newApp.token);
              localStorage.setItem('mentorshipApplicationMentorId', mentorUser.id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error saving application progress:', error);
    }
  }, [applicationForm.values, mentorUser.id, plan, applicationToken, user]);

  const handleBeforeLinkedInRedirect = useCallback(async () => {
    // Save current step so we can restore after redirect
    sessionStorage.setItem('application_step_before_linkedin', currentStep);
    // Save application progress
    await saveApplicationProgress();
  }, [currentStep, saveApplicationProgress]);

  const handleNextStep = useCallback(async () => {
    if (stepLoading) return;
    
    setStepLoading(true);
    
    try {
      const currentStepKey = currentStep as keyof ApplicationForm;
      
      if (['mentorshipGoal', 'mentorshipTimeline', 'message'].includes(currentStep)) {
        const error = applicationForm.validateField(currentStepKey).error;
        if (error) {
          setStepLoading(false);
          return;
        }
        
        try {
          await saveApplicationProgress();
        } catch (saveError) {
          setStepLoading(false);
          return;
        }
      }

      // Before advancing from message step, validate with AI
      if (currentStep === 'message' && !validationChecked) {
        const goalError = applicationForm.validateField('mentorshipGoal').error;
        const timelineError = applicationForm.validateField('mentorshipTimeline').error;
        const messageError = applicationForm.validateField('message').error;
        
        if (goalError || timelineError || messageError) {
          setStepLoading(false);
          return;
        }
        
        // Run AI validation
        setIsValidatingMessage(true);
        try {
          const response = await fetch('/api/mentorship/validate-message', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: applicationForm.values.message }),
          });
          
          if (response.ok) {
            const validation = await response.json();
            setMessageValidation(validation);
            setValidationChecked(true);
            
            // If message needs improvement, stop here and show feedback
            if (!validation.isValid) {
              setStepLoading(false);
              setIsValidatingMessage(false);
              return;
            }
          }
        } catch (error) {
          console.error('Validation error:', error);
          // Allow proceeding if validation fails
        } finally {
          setIsValidatingMessage(false);
        }
      }

      const nextIndex = currentStepIndex + 1;

      if (nextIndex < allSteps.length) {
        setCurrentStep(allSteps[nextIndex] as FlowStep);
        // Scroll to top after step change
        setTimeout(() => scrollToTop(), 100);
      }
    } finally {
      setStepLoading(false);
    }
  }, [stepLoading, currentStep, currentStepIndex, applicationForm, allSteps, saveApplicationProgress, validationChecked, scrollToTop]);
  
  const handleSkipValidation = () => {
    setValidationChecked(true);
    handleNextStep();
  };

  // Remove auto-advance - make all navigation manual

  const handlePreviousStep = () => {
    // Reset only auth/verification/payment specific states when going back
    // Do NOT reset form data (applicationForm values should persist)
    if (currentStep === 'auth') {
      setAuthError('');
      setAuthLoading(false);
    }
    if (currentStep === 'emailVerification') {
      setEmailVerificationSent(false);
      setEmailVerified(false);
      setAuthError('');
    } else if (currentStep === 'payment') {
      // Don't reset paymentSetup to false - keep it if already set up
      setPaymentError('');
    }
    
    let targetIndex = currentStepIndex - 1;
    
    // Skip steps that are no longer needed
    while (targetIndex >= 0) {
      const targetStep = allSteps[targetIndex];
      
      // Skip auth step if user is already signed in
      if (targetStep === 'auth' && user) {
        targetIndex--;
        continue;
      }
      
      // Skip email verification step if user's email is already verified
      if (targetStep === 'emailVerification' && user?.emailVerified) {
        targetIndex--;
        continue;
      }
      
      // Skip payment step if payment is already set up
      if (targetStep === 'payment' && paymentSetup) {
        targetIndex--;
        continue;
      }
      
      // Found a valid step to go back to
      break;
    }
    
    if (targetIndex >= 0) {
      setCurrentStep(allSteps[targetIndex]);
      // Scroll to top after step change
      setTimeout(() => scrollToTop(), 100);
    }
  };

  const handleAuth = async (values: AuthForm) => {
    setAuthLoading(true);
    setAuthError('');

    try {
      if (authMode === 'signup') {
        // Check Turnstile token
        if (!turnstileToken) {
          throw new Error('Please complete the verification challenge.');
        }

        // Register user
        const registerResponse = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...values, turnstileToken }),
        });

        const registerData = await registerResponse.json();
        if (!registerResponse.ok) {
          throw new Error(registerData.error || 'Failed to sign up');
        }
      }

      // Sign in
      const signInResult = await signIn('credentials', {
        email: values.email,
        password: values.password,
        redirect: false,
      });

      if (signInResult?.error) {
        throw new Error(authMode === 'signup' ? 'Failed to sign in after registration' : 'Invalid credentials');
      }

      // Wait for user context to update after sign in
      await new Promise(resolve => setTimeout(resolve, 1000));
      await refresh();

      // Link application if we have a token
      if (applicationToken) {
        try {
          await fetch('/api/mentorshipApplications/link', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: applicationToken }),
          });
          localStorage.removeItem('mentorshipApplicationToken');
          setApplicationToken(null);
        } catch (error) {
          console.error('Error linking application:', error);
        }
      }

      // Move to next step
      setCurrentStep('emailVerification');
      setTimeout(() => scrollToTop(), 100);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setAuthLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    try {
      const response = await fetch('/api/auth/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          applicationToken,
          mentorHandle: mentorUser.handle 
        })
      });
      
      if (response.ok) {
        setEmailVerificationSent(true);
        setAuthError('');
      } else {
        const data = await response.json();
        setAuthError(data.error || 'Failed to send verification email');
      }
    } catch (error) {
      setAuthError('Failed to send verification email. Please try again.');
    } finally {
      setStepLoading(false);
    }
  };

  const checkEmailVerification = async () => {
    try {
      // Use current-user endpoint to get fresh session data
      const response = await fetch('/api/auth/current-user', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.emailVerified) {
          setEmailVerified(true);
          // Refresh the user context to get updated verification status
          await refresh();
          // Small delay to ensure context updates
          setTimeout(async () => {
            await refresh();
            setCurrentStep('payment');
            scrollToTop();
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentSetup(true);
    setPaymentError('');
    setCurrentStep('review');
    setTimeout(() => scrollToTop(), 100);
  };

  const handleFinalSubmit = async () => {
    setStepLoading(true);
    
    try {
      await saveApplicationProgress();
      
      // Submit the application
      const params = new URLSearchParams({
        mentorUserId: mentorUser.id,
        menteeUserId: user?.id || '',
      });
      const getResponse = await fetch(`/api/mentorshipApplications?${params}`);
      const applications = await getResponse.json();
      
      if (applications.length > 0) {
        const updateResponse = await fetch(`/api/mentorshipApplications/${applications[0].id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'PENDING_MENTOR' }),
        });
        
        const result = await updateResponse.json();
        if (result.error) {
          alert(result.error);
          setStepLoading(false);
          return;
        }
        
        // Clear token and mentor ID from localStorage
        localStorage.removeItem('mentorshipApplicationToken');
        localStorage.removeItem('mentorshipApplicationMentorId');
        
        // Success - redirect to messages
        window.location.href = `/learning/messages/${mentorUser.id}`;
      }
    } catch (error) {
      alert('Error submitting application. Please try again.');
      setStepLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (blockingError) {
    return (
      <Center h={400}>
        <Paper p="xl" radius="md" withBorder maw={500}>
          <Stack align="center" gap="md">
            <Text size="xl" fw={600} c="red">Unable to Apply</Text>
            <Text size="md" ta="center">{blockingError}</Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <div className={classes.container}>
      {/* Mobile sticky header - only visible on mobile */}
      <MobileApplicationHeader mentorUser={mentorUser} />

      <Group align="flex-start" gap="xl" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Desktop Left Sidebar - Mentor Profile */}
        <Box style={{ width: '300px', flexShrink: 0 }} className={classes.desktopProfile}>
          <Paper 
            p="lg" 
            radius="md" 
            withBorder 
            style={{ 
              position: 'sticky', 
              top: '20px',
              background: 'white',
              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
            }}
          >
            <Stack align="center" gap="sm">
              <Avatar
                userId={mentorUser.id}
                avatarId={mentorUser.avatarId}
                size={140}
                radius={100}
                className={classes.desktopAvatar}
              />
              
              <Stack gap={4} align="center" style={{ textAlign: 'center' }}>
                <Text size="lg" fw={600}>{mentorUser.name}</Text>
                {mentorUser.mentor?.professionalTagline && (
                  <Text size="sm" c="dimmed" fw={500} fs="italic" style={{ marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                    {mentorUser.mentor.professionalTagline}
                  </Text>
                )}
                {mentorUser.location && (
                  <Badge 
                    variant="light" 
                    color="gray" 
                    size="xs"
                    leftSection={<IconMapPin size={12} />}
                    styles={{
                      root: { paddingLeft: 6, paddingRight: 6 },
                      label: { fontWeight: 600, letterSpacing: 0.2 },
                    }}
                  >
                    {mentorUser.location}
                  </Badge>
                )}
              </Stack>
              
              {mentorUser.mentor?.bio && (
                <Text
                  size="sm"
                  c="dimmed"
                  ta="center"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {mentorUser.mentor.bio}
                </Text>
              )}
              
              {/* Badges section with reduced spacing */}
              <Stack gap={8} align="center">
                {/* Expertise badges */}
                <ExpertiseBadgeGroup expertises={mentorUser.mentor?.expertises ?? []} />

                {/* User badges */}
                <UserBadgeGroup badges={mentorUser.badges ?? []} />
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Right Content - Application Form */}
        <Box style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
        <div style={{ padding: '0 3rem 0.5rem' }} className={classes.desktopOnly}>
          <h1 className={classes.title} style={{ marginTop: 0, marginBottom: '1rem' }}>
            Apply for Mentorship with {mentorUser.name}
          </h1>
          <p className={classes.subtitle}>
            Complete your application in a few simple steps
          </p>
          <Progress
            value={progress}
            size="lg"
            radius="xl"
            className={classes.progressBar}
          />
        </div>

        <div style={{ padding: '0 3rem' }} className={classes.desktopOnly}>
        <Paper className={classes.stepCard}>
        {/* Mentorship Goal Step */}
        {currentStep === 'mentorshipGoal' && (
          <div data-testid="goal-step">
            <MentorshipGoalStep
              value={applicationForm.values.mentorshipGoal}
              onChange={(value) => applicationForm.setFieldValue('mentorshipGoal', value)}
              onAutoAdvance={handleNextStep}
              error={applicationForm.errors.mentorshipGoal as string}
              mentorName={mentorUser.name}
            />
          </div>
        )}

        {/* Mentorship Timeline Step */}
        {currentStep === 'mentorshipTimeline' && (
          <MentorshipTimelineStep
            value={applicationForm.values.mentorshipTimeline}
            onChange={(value) => {
              applicationForm.setFieldValue('mentorshipTimeline', value);
            }}
            onAutoAdvance={() => {
              // Don't auto-advance, let useEffect handle it
            }}
            error={applicationForm.errors.mentorshipTimeline as string}
          />
        )}

        {/* Message Step */}
        {currentStep === 'message' && (
          <MessageStep
            value={applicationForm.values.message}
            onChange={(value) => {
              applicationForm.setFieldValue('message', value);
              // Reset validation when user edits
              if (validationChecked) {
                setValidationChecked(false);
                setMessageValidation(null);
              }
            }}
            error={applicationForm.errors.message as string}
            mentorName={mentorUser.name}
            validation={messageValidation}
            isValidating={isValidatingMessage}
          />
        )}

        {/* Authentication Step */}
        {currentStep === 'auth' && (
          <AuthStep
            authMode={authMode}
            onAuthModeChange={(mode) => {
              setAuthMode(mode);
              setAuthError('');
            }}
            onSubmit={handleAuth}
            authError={authError}
            authLoading={authLoading}
            formValues={authForm.values}
            formErrors={authForm.errors}
            onFieldChange={(field, value) => authForm.setFieldValue(field, value)}
            mentorId={mentorUser.id}
            turnstileToken={turnstileToken}
            onTurnstileChange={setTurnstileToken}
          />
        )}

        {/* Email Verification Step */}
        {currentStep === 'emailVerification' && (
          <EmailVerificationStep
            userEmail={user?.email}
            emailVerificationSent={emailVerificationSent}
            emailVerified={emailVerified}
            onSendVerification={sendVerificationEmail}
            onCheckVerification={checkEmailVerification}
            authError={authError}
            loading={stepLoading}
          />
        )}

        {/* LinkedIn Step */}
        {currentStep === 'linkedin' && (
          <LinkedInStep
            mentorId={mentorUser.id}
            mentorHandle={mentorUser.mentor?.handle}
            onNext={() => setCurrentStep('payment')}
            onSkip={() => setCurrentStep('payment')}
            linkedinUrl={authForm.values.linkedinUrl}
            onLinkedInConnected={(url) => authForm.setFieldValue('linkedinUrl', url)}
            onBeforeRedirect={handleBeforeLinkedInRedirect}
          />
        )}

        {/* Payment Step */}
        {currentStep === 'payment' && (
          <PaymentStep
            onPaymentSetup={handlePaymentSuccess}
            paymentError={paymentError}
          />
        )}

        {currentStep === 'review' && (
          <div>
            <ReviewStep
              mentorName={mentorUser.name}
              answersJson={Object.entries(applicationForm.values).map(([key, value]) => ({
                question: key,
                answer: value,
              }))}
              onSubmit={handleFinalSubmit}
              loading={stepLoading}
            />
          </div>
        )}
        </Paper>
        </div>

        <div className={`${classes.mobileOnly} ${classes.mobileFormContainer}`}>
        <div className={classes.stepCard}>
        {/* Mentorship Goal Step */}
        {currentStep === 'mentorshipGoal' && (
          <div data-testid="goal-step">
            <MentorshipGoalStep
              value={applicationForm.values.mentorshipGoal}
              onChange={(value) => applicationForm.setFieldValue('mentorshipGoal', value)}
              onAutoAdvance={handleNextStep}
              error={applicationForm.errors.mentorshipGoal as string}
              mentorName={mentorUser.name}
            />
          </div>
        )}

        {/* Mentorship Timeline Step */}
        {currentStep === 'mentorshipTimeline' && (
          <MentorshipTimelineStep
            value={applicationForm.values.mentorshipTimeline}
            onChange={(value) => applicationForm.setFieldValue('mentorshipTimeline', value)}
            onAutoAdvance={handleNextStep}
            error={applicationForm.errors.mentorshipTimeline as string}
          />
        )}

        {/* Message Step */}
        {currentStep === 'message' && (
          <MessageStep
            value={applicationForm.values.message}
            onChange={(value) => {
              applicationForm.setFieldValue('message', value);
              // Reset validation when user edits
              if (validationChecked) {
                setValidationChecked(false);
                setMessageValidation(null);
              }
            }}
            error={applicationForm.errors.message as string}
            mentorName={mentorUser.name}
            validation={messageValidation}
            isValidating={isValidatingMessage}
          />
        )}

        {/* Authentication Step */}
        {currentStep === 'auth' && (
          <AuthStep
            authMode={authMode}
            onAuthModeChange={(mode) => {
              setAuthMode(mode);
              setAuthError('');
            }}
            onSubmit={handleAuth}
            authError={authError}
            authLoading={authLoading}
            formValues={authForm.values}
            formErrors={authForm.errors}
            onFieldChange={(field, value) => authForm.setFieldValue(field, value)}
            mentorId={mentorUser.id}
            turnstileToken={turnstileToken}
            onTurnstileChange={setTurnstileToken}
          />
        )}

        {/* Email Verification Step */}
        {currentStep === 'emailVerification' && (
          <EmailVerificationStep
            userEmail={user?.email}
            emailVerificationSent={emailVerificationSent}
            emailVerified={emailVerified}
            onSendVerification={sendVerificationEmail}
            onCheckVerification={checkEmailVerification}
            authError={authError}
            loading={stepLoading}
          />
        )}

        {/* LinkedIn Step */}
        {currentStep === 'linkedin' && (
          <LinkedInStep
            mentorId={mentorUser.id}
            mentorHandle={mentorUser.mentor?.handle}
            onNext={() => setCurrentStep('payment')}
            onSkip={() => setCurrentStep('payment')}
            linkedinUrl={authForm.values.linkedinUrl}
            onLinkedInConnected={(url) => authForm.setFieldValue('linkedinUrl', url)}
            onBeforeRedirect={handleBeforeLinkedInRedirect}
          />
        )}

        {/* Payment Step */}
        {currentStep === 'payment' && (
          <div className={classes.paymentSection}>
            <PaymentStep
              onPaymentSetup={handlePaymentSuccess}
              paymentError={paymentError}
            />
          </div>
        )}

        {/* Review Step */}
        {currentStep === 'review' && (
          <div className={classes.reviewSection}>
            <ReviewStep
              mentorName={mentorUser.name}
              answersJson={Object.entries(applicationForm.values).map(([key, value]) => ({
                question: key,
                answer: value,
              }))}
              onSubmit={handleFinalSubmit}
              loading={stepLoading}
            />
          </div>
        )}
        </div>
        </div>

        {/* Navigation */}
        <div style={{ padding: '0 3rem' }} className={classes.desktopOnly}>
          <Group justify="space-between" className={classes.navigation}>
            {currentStepIndex > 0 ? (
              <Button 
                variant="outline" 
                onClick={handlePreviousStep} 
                className={classes.navButton}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {(['mentorshipGoal', 'mentorshipTimeline', 'message'].includes(currentStep)) && (
              <Group gap="sm">
                {currentStep === 'message' && messageValidation && !messageValidation.isValid && !isValidatingMessage && (
                  <Button 
                    variant="subtle"
                    onClick={handleSkipValidation}
                    disabled={stepLoading}
                  >
                    Skip and Continue
                  </Button>
                )}
                <Button 
                  onClick={handleNextStep} 
                  loading={stepLoading}
                  className={classes.navButton}
                >
                  Next
                </Button>
              </Group>
            )}
          </Group>
        </div>

        {/* Mobile Navigation */}
        <div className={`${classes.mobileOnly} ${classes.mobileFormContainer}`}>
          <Group justify="space-between" className={classes.navigation}>
            {currentStepIndex > 0 ? (
              <Button 
                variant="outline" 
                onClick={handlePreviousStep} 
                className={classes.navButton}
              >
                Back
              </Button>
            ) : (
              <div />
            )}

            {(['mentorshipGoal', 'mentorshipTimeline', 'message'].includes(currentStep)) && (
              <Group gap="sm">
                {currentStep === 'message' && messageValidation && !messageValidation.isValid && !isValidatingMessage && (
                  <Button 
                    variant="subtle"
                    onClick={handleSkipValidation}
                    disabled={stepLoading}
                    size="sm"
                  >
                    Skip
                  </Button>
                )}
                <Button 
                  onClick={handleNextStep} 
                  loading={stepLoading}
                  className={classes.navButton}
                >
                  Next
                </Button>
              </Group>
            )}
          </Group>
        </div>
        </Box>
      </Group>
    </div>
  );
}
